import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Archive,
  ArchiveRestore,
  Inbox,
  Mail,
  MailOpen,
  Pin,
  PinOff,
  Search,
  SearchX,
  X,
} from "lucide-react";
import { type DialogSummary, mediaLabel, useUpdateDialog } from "@/lib/inbox";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { DialogListItem } from "./DialogListItem";
import { InboxAccountFilter } from "./InboxAccountFilter";
import { InboxCampaignFilter } from "./InboxCampaignFilter";
import { LeadMenuItems, LeadStatusFilter } from "./LeadStatus";
import { useDialogPreviews } from "./useDialogPreview";
import type { InboxList } from "./useInboxList";

export interface InboxDialogListProps {
  list: InboxList;
  /** Runs after a dialog is picked — the page uses it to open the thread pane. */
  onSelect?: (dialog: DialogSummary) => void;
  /** The quick inbox: without the campaign filter, to leave the list its room. */
  compact?: boolean;
}

/**
 * The left-hand side of an inbox: the search, the filters, the switch to the
 * archive and the scrolling list of dialogs. Shared verbatim by InboxPage and
 * InboxDrawer; the parent supplies the column (a flex column with `min-h-0`)
 * this fills.
 */
export function InboxDialogList({ list, onSelect, compact = false }: InboxDialogListProps) {
  const { t } = useTranslation();
  const getPreview = useDialogPreviews();
  const update = useUpdateDialog();
  // The row menu — a right click on a row, or its "⋯". One menu for the whole
  // list, opened at the point it was asked for. Closing keeps the dialog: the
  // menu is still on screen while it fades out.
  const [menu, setMenu] = useState<{ dialog: DialogSummary; x: number; y: number } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  // The dialog as the list has it now: the menu can stay open over a change.
  const menuDialog = menu && (list.visible?.find((d) => d.id === menu.dialog.id) ?? menu.dialog);

  return (
    <>
      <div className="flex shrink-0 flex-wrap gap-2 border-b border-border p-2">
        <div className="flex w-full gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              size={15}
              strokeWidth={2}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
            />
            <Input
              type="search"
              value={list.search}
              onChange={(e) => list.setSearch(e.target.value)}
              maxLength={100}
              placeholder={t("inbox.search_placeholder")}
              aria-label={t("inbox.search_placeholder")}
              title={t("inbox.search_hint")}
              className="bg-bg-primary px-9 [&::-webkit-search-cancel-button]:hidden"
            />
            {list.search && (
              <button
                type="button"
                onClick={() => list.setSearch("")}
                aria-label={t("inbox.search_clear")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <X size={14} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => list.setArchiveView(!list.archiveView)}
            aria-pressed={list.archiveView}
            aria-label={t("inbox.archive_view")}
            title={t("inbox.archive_view")}
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
              list.archiveView
                ? "border-accent/50 bg-accent/15 text-accent"
                : "border-border bg-bg-primary text-fg-muted hover:text-fg-primary",
            )}
          >
            <Archive size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <InboxAccountFilter
          accounts={list.accounts}
          value={list.accountFilter}
          onChange={list.setAccountFilter}
          className="min-w-[8.5rem] flex-1"
        />
        <LeadStatusFilter
          value={list.statusFilter}
          onChange={list.setStatusFilter}
          className="min-w-[8.5rem] flex-1"
        />
        {!compact && (
          <InboxCampaignFilter
            value={list.campaignFilter}
            onChange={list.setCampaignFilter}
            className="w-full"
          />
        )}
        {list.archiveView && (
          <p className="w-full px-1 text-xs text-fg-muted">
            {t("inbox.archive_banner")}{" "}
            <button
              type="button"
              onClick={() => list.setArchiveView(false)}
              className="rounded-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t("inbox.archive_back")}
            </button>
          </p>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {list.isLoading ? (
          <p className="p-3 text-sm text-fg-muted">{t("inbox.loading")}</p>
        ) : list.visible && list.visible.length > 0 ? (
          <ul className="space-y-1">
            {list.visible.map((d) => {
              const preview = getPreview(d.id);
              return (
                <DialogListItem
                  key={d.id}
                  dialog={d}
                  selected={d.id === list.selected?.id}
                  // Found by the text of a message: show that message.
                  preview={d.match_text ?? preview?.text}
                  previewMedia={
                    !d.match_text && preview?.hasMedia ? mediaLabel(preview, t) : null
                  }
                  onClick={() => {
                    list.select(d);
                    onSelect?.(d);
                  }}
                  onMenu={(at) => {
                    setMenu({ dialog: d, ...at });
                    setMenuOpen(true);
                  }}
                />
              );
            })}
            {list.hasMore && (
              <li>
                <Button size="sm" variant="ghost" className="w-full" onClick={list.showMore}>
                  {t("inbox.show_more")}
                </Button>
              </li>
            )}
          </ul>
        ) : list.filtered ? (
          // Nobody answered and nothing matches are different news.
          <EmptyState
            icon={SearchX}
            title={t("inbox.filtered_empty_title")}
            description={t("inbox.filtered_empty")}
            action={
              <Button size="sm" variant="outline" onClick={list.resetFilters}>
                {t("inbox.reset_filters")}
              </Button>
            }
            // The account filter picks from the rows that are loaded: this
            // account's dialogs may all be further down.
            secondaryAction={
              list.hasMore && (
                <Button size="sm" variant="ghost" onClick={list.showMore}>
                  {t("inbox.show_more")}
                </Button>
              )
            }
          />
        ) : list.archiveView ? (
          <EmptyState
            icon={Archive}
            title={t("inbox.archive_empty_title")}
            description={t("inbox.archive_empty")}
          />
        ) : (
          <EmptyState icon={Inbox} title={t("inbox.empty_title")} description={t("inbox.empty")} />
        )}
      </div>

      <DropdownMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        // Not modal: a right click on another row moves the menu there.
        modal={false}
      >
        {/* The menu hangs off an invisible point. It lives in <body>: inside
            the drawer, which is moved with a transform, `fixed` would count
            from the drawer's corner instead of the screen's. */}
        {createPortal(
          <DropdownMenuTrigger asChild>
            <span
              aria-hidden="true"
              className="pointer-events-none fixed h-0 w-0"
              style={{ left: menu?.x ?? 0, top: menu?.y ?? 0 }}
            />
          </DropdownMenuTrigger>,
          document.body,
        )}
        <DropdownMenuContent align="start">
          {menuDialog && (
            <>
              <LeadMenuItems dialog={menuDialog} />
              <DropdownMenuSeparator />
              {!menuDialog.archived && (
                <DropdownMenuItem
                  icon={menuDialog.pinned ? <PinOff size={15} /> : <Pin size={15} />}
                  onSelect={() =>
                    update.mutate({ dialogId: menuDialog.id, pinned: !menuDialog.pinned })
                  }
                >
                  {t(menuDialog.pinned ? "inbox.unpin" : "inbox.pin")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                icon={menuDialog.unread_count > 0 ? <MailOpen size={15} /> : <Mail size={15} />}
                onSelect={() =>
                  update.mutate({ dialogId: menuDialog.id, unread: menuDialog.unread_count === 0 })
                }
              >
                {t(menuDialog.unread_count > 0 ? "inbox.mark_read" : "inbox.mark_unread")}
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={menuDialog.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                onSelect={() => list.archive(menuDialog)}
              >
                {t(menuDialog.archived ? "inbox.unarchive" : "inbox.archive")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
InboxDialogList.displayName = "InboxDialogList";
